// ---------------------------------------------------------------------------
// Action Registry — maps 40+ action types to visual config + form keys.
//
// Palette groups distinguish in-app navigation, external links, direct events,
// and awaitable UI motion while preserving the legacy visual categories.
//
// Merges hardcoded configs with ontology (action-types.json) for any
// new action types. Hardcoded entries always win on conflict.
// ---------------------------------------------------------------------------

import type { VisualCategory } from '@/features/flow-editor/models/flow-models'
import actionsJson from '@cogo/schema/definitions/registry/actions.json' with { type: 'json' }
import ontologyJson from '@cogo/validator/ontology/action-types.json' with { type: 'json' }

export interface ActionTypeConfig {
  label: string
  icon: string
  category: VisualCategory
  badgeColor: string
  formKey: string
  description: string
}

export interface ActionCategoryGroup {
  label: string
  icon: string
  color: string
  actions: string[]
}

// ---- Ontology types ----

interface OntologyAction {
  category: string
  description: string
  requiredParams: (string | string[])[]
  optionalParams: string[]
  tags?: string[]
}

interface OntologyData {
  actions: Record<string, OntologyAction>
}

const ontology = ontologyJson as OntologyData

/** Get ontology definition for an action type (camelCase key). */
export function getOntologyAction(actionType: string): OntologyAction | undefined {
  // Try direct match first, then try camelCase conversion from snake_case
  if (ontology.actions[actionType]) return ontology.actions[actionType]
  const camel = actionType
    .replace(/_action$/, '')
    .replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())
  return ontology.actions[camel]
}

/** Count required + optional params from ontology. */
export function getOntologyParamCounts(actionType: string): { required: number; optional: number } | null {
  const def = getOntologyAction(actionType)
  if (!def) return null
  return {
    required: def.requiredParams.length,
    optional: def.optionalParams.length,
  }
}

// ---- Category mapping from actions.json category → VisualCategory ----

const CATEGORY_MAP: Record<string, VisualCategory> = {
  data: 'dataFetch',
  navigation: 'navigation',
  ui: 'uiFeedback',
  state: 'dataMutate',
  'flow-control': 'flowControl',
  flow: 'flowControl',
  auth: 'auth',
  storage: 'utility',
  form: 'form',
  crypto: 'utility',
  media: 'utility',
  device: 'utility',
  analytics: 'utility',
  commerce: 'utility',
  i18n: 'utility',
  utility: 'utility',
}

// ---- Badge color mapping from VisualCategory ----

const CATEGORY_BADGE_MAP: Record<string, string> = {
  dataFetch: 'bg-primary',
  dataMutate: 'bg-primary',
  navigation: 'bg-blue-500',
  uiFeedback: 'bg-indigo-400',
  form: 'bg-violet-500',
  flowControl: 'bg-amber-500',
  auth: 'bg-rose-500',
  utility: 'bg-gray-500',
  general: 'bg-gray-500',
  // Legacy
  apiData: 'bg-primary',
  uiAction: 'bg-indigo-400',
}

// ---- Default icon mapping from actions.json category ----

const CATEGORY_ICON_MAP: Record<string, string> = {
  data: 'Database',
  navigation: 'Compass',
  ui: 'Layout',
  state: 'Variable',
  'flow-control': 'GitBranch',
  flow: 'GitBranch',
  auth: 'Shield',
  storage: 'HardDrive',
  form: 'CheckSquare',
  crypto: 'Lock',
  media: 'Camera',
  device: 'Smartphone',
  analytics: 'BarChart',
  commerce: 'CreditCard',
  i18n: 'Globe2',
  utility: 'Wrench',
}

// ---- camelCase → Title Case ----

function camelToTitle(camelCase: string): string {
  return camelCase
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim()
}

// ---- Build dynamic configs from actions.json ----

interface ActionsJsonEntry {
  flutter: string | null
  level: string
  tier: string
  category: string
  platforms: Record<string, boolean>
  params: Record<string, string>
  description: string
}

function buildDynamicRegistry(): Record<string, ActionTypeConfig> {
  const dynamic: Record<string, ActionTypeConfig> = {}
  const actions = actionsJson.actions as Record<string, ActionsJsonEntry>

  for (const [jsKey, entry] of Object.entries(actions)) {
    const registryKey = entry.flutter ?? jsKey
    const visualCategory = CATEGORY_MAP[entry.category] ?? 'utility'

    dynamic[registryKey] = {
      label: camelToTitle(jsKey),
      icon: CATEGORY_ICON_MAP[entry.category] ?? 'Play',
      category: visualCategory,
      badgeColor: CATEGORY_BADGE_MAP[visualCategory] ?? 'bg-gray-500',
      formKey: 'generic',
      description: entry.description,
    }
  }

  return dynamic
}

// ---- Curated Hardcoded Configs (icons, colors, form keys) ----

const HARDCODED_REGISTRY: Record<string, ActionTypeConfig> = {
  // Data Fetch
  'restful_data_action': { label: 'API Call', icon: 'Globe', category: 'dataFetch', badgeColor: 'bg-primary', formKey: 'apiCall', description: 'Make HTTP request' },
  'graphql_action': { label: 'GraphQL', icon: 'Database', category: 'dataFetch', badgeColor: 'bg-primary', formKey: 'apiCall', description: 'GraphQL query/mutation' },
  'supabase_select_action': { label: 'DB Select', icon: 'Search', category: 'dataFetch', badgeColor: 'bg-primary', formKey: 'supabase', description: 'Select rows from table' },
  'gemini_action': { label: 'AI Generate', icon: 'Sparkles', category: 'dataFetch', badgeColor: 'bg-primary', formKey: 'generic', description: 'Generate with AI' },

  // Data Mutate — Bundle Actions
  'upsertWhere': { label: 'Upsert Where', icon: 'Replace', category: 'dataMutate', badgeColor: 'bg-primary', formKey: 'generic', description: 'Insert or update array item by match expression' },
  'compute': { label: 'Compute', icon: 'Calculator', category: 'dataMutate', badgeColor: 'bg-primary', formKey: 'expression', description: 'Compute expression and assign to target' },
  'updateWhere': { label: 'Update Where', icon: 'Pencil', category: 'dataMutate', badgeColor: 'bg-primary', formKey: 'generic', description: 'Update array items matching condition' },
  'removeWhere': { label: 'Remove Where', icon: 'Minus', category: 'dataMutate', badgeColor: 'bg-primary', formKey: 'generic', description: 'Remove array items matching condition' },
  'scrollToPosition': { label: 'Scroll To', icon: 'ArrowDown', category: 'uiFeedback', badgeColor: 'bg-indigo-400', formKey: 'generic', description: 'Scroll to position' },

  // Data Mutate — Supabase
  'supabase_insert_action': { label: 'DB Insert', icon: 'Plus', category: 'dataMutate', badgeColor: 'bg-primary', formKey: 'supabase', description: 'Insert row into table' },
  'supabase_update_action': { label: 'DB Update', icon: 'Pencil', category: 'dataMutate', badgeColor: 'bg-primary', formKey: 'supabase', description: 'Update rows in table' },
  'supabase_delete_action': { label: 'DB Delete', icon: 'Trash2', category: 'dataMutate', badgeColor: 'bg-primary', formKey: 'supabase', description: 'Delete rows from table' },
  'set_state_action': { label: 'Set State', icon: 'Variable', category: 'dataMutate', badgeColor: 'bg-primary', formKey: 'setState', description: 'Update state variable' },
  'expression_action': { label: 'Expression', icon: 'Code', category: 'dataMutate', badgeColor: 'bg-primary', formKey: 'expression', description: 'Evaluate expression' },

  // Navigation
  'navigation_action': { label: 'Navigate', icon: 'Compass', category: 'navigation', badgeColor: 'bg-blue-500', formKey: 'navigation', description: 'Navigate to page' },
  'go_back_action': { label: 'Go Back', icon: 'ArrowLeft', category: 'navigation', badgeColor: 'bg-blue-500', formKey: 'navigation', description: 'Go to previous page' },
  'open_link_action': { label: 'Open Link', icon: 'ExternalLink', category: 'navigation', badgeColor: 'bg-blue-500', formKey: 'navigation', description: 'Open URL in browser' },
  'emitEvent': { label: 'Emit Event', icon: 'Zap', category: 'uiFeedback', badgeColor: 'bg-indigo-400', formKey: 'generic', description: 'Emit one atomic app event directly' },
  'runMotion': { label: 'Run Motion', icon: 'RefreshCw', category: 'uiFeedback', badgeColor: 'bg-indigo-400', formKey: 'generic', description: 'Animate an element and wait until motion completes' },

  // UI Feedback
  'show_popup_action': { label: 'Show Popup', icon: 'MessageSquare', category: 'uiFeedback', badgeColor: 'bg-indigo-400', formKey: 'popup', description: 'Show popup dialog' },
  'close_popup_action': { label: 'Close Popup', icon: 'X', category: 'uiFeedback', badgeColor: 'bg-indigo-400', formKey: 'generic', description: 'Close popup dialog' },
  'show_toast_action': { label: 'Show Toast', icon: 'Bell', category: 'uiFeedback', badgeColor: 'bg-indigo-400', formKey: 'popup', description: 'Show toast notification' },
  'open_drawer_action': { label: 'Open Drawer', icon: 'PanelLeft', category: 'uiFeedback', badgeColor: 'bg-indigo-400', formKey: 'popup', description: 'Open drawer panel' },
  'close_drawer_action': { label: 'Close Drawer', icon: 'PanelLeftClose', category: 'uiFeedback', badgeColor: 'bg-indigo-400', formKey: 'generic', description: 'Close drawer panel' },
  'set_property_action': { label: 'Set Property', icon: 'Settings', category: 'uiFeedback', badgeColor: 'bg-indigo-400', formKey: 'setState', description: 'Set element property' },
  'refresh_action': { label: 'Refresh', icon: 'RefreshCw', category: 'uiFeedback', badgeColor: 'bg-indigo-400', formKey: 'generic', description: 'Refresh data/UI' },

  // Form
  'form_validate_action': { label: 'Validate Form', icon: 'CheckSquare', category: 'form', badgeColor: 'bg-violet-500', formKey: 'generic', description: 'Validate form fields' },
  'form_submit_action': { label: 'Submit Form', icon: 'Send', category: 'form', badgeColor: 'bg-violet-500', formKey: 'generic', description: 'Submit form data' },
  'form_reset_action': { label: 'Reset Form', icon: 'RotateCcw', category: 'form', badgeColor: 'bg-violet-500', formKey: 'generic', description: 'Reset form to defaults' },

  // Flow Control
  'conditional_action': { label: 'If/Else', icon: 'GitBranch', category: 'flowControl', badgeColor: 'bg-amber-500', formKey: 'conditional', description: 'Conditional branching' },
  'loop_action': { label: 'Loop', icon: 'Repeat', category: 'flowControl', badgeColor: 'bg-amber-500', formKey: 'loop', description: 'Iterate over collection' },
  'switch_action': { label: 'Switch', icon: 'Route', category: 'flowControl', badgeColor: 'bg-amber-500', formKey: 'switch', description: 'Multi-branch selection' },
  'delay_action': { label: 'Delay', icon: 'Timer', category: 'flowControl', badgeColor: 'bg-amber-500', formKey: 'generic', description: 'Wait before continuing' },
  'break_action': { label: 'Break', icon: 'StopCircle', category: 'flowControl', badgeColor: 'bg-amber-500', formKey: 'generic', description: 'Break out of loop' },
  'run_action_flow_action': { label: 'Call Flow', icon: 'Play', category: 'flowControl', badgeColor: 'bg-amber-500', formKey: 'callFlow', description: 'Execute another flow' },

  // Auth
  'login_action': { label: 'Login', icon: 'LogIn', category: 'auth', badgeColor: 'bg-rose-500', formKey: 'auth', description: 'User login' },
  'logout_action': { label: 'Logout', icon: 'LogOut', category: 'auth', badgeColor: 'bg-rose-500', formKey: 'auth', description: 'User logout' },
  'register_action': { label: 'Register', icon: 'UserPlus', category: 'auth', badgeColor: 'bg-rose-500', formKey: 'auth', description: 'User registration' },

  // Utility
  'storage_get_action': { label: 'Get Storage', icon: 'HardDrive', category: 'utility', badgeColor: 'bg-gray-500', formKey: 'storage', description: 'Read from storage' },
  'storage_set_action': { label: 'Set Storage', icon: 'Save', category: 'utility', badgeColor: 'bg-gray-500', formKey: 'storage', description: 'Write to storage' },
  'storage_remove_action': { label: 'Remove Storage', icon: 'Trash', category: 'utility', badgeColor: 'bg-gray-500', formKey: 'storage', description: 'Delete from storage' },
  'hash_action': { label: 'Hash', icon: 'Hash', category: 'utility', badgeColor: 'bg-gray-500', formKey: 'generic', description: 'Hash data' },
  'encrypt_action': { label: 'Encrypt', icon: 'Lock', category: 'utility', badgeColor: 'bg-gray-500', formKey: 'generic', description: 'Encrypt data' },
  'decrypt_action': { label: 'Decrypt', icon: 'Unlock', category: 'utility', badgeColor: 'bg-gray-500', formKey: 'generic', description: 'Decrypt data' },
  'console_log_action': { label: 'Console Log', icon: 'Terminal', category: 'utility', badgeColor: 'bg-gray-500', formKey: 'expression', description: 'Log to console' },
}

// ---- Merged Registry: dynamic (base) + hardcoded (wins on conflict) ----

export const actionRegistry: Record<string, ActionTypeConfig> = {
  ...buildDynamicRegistry(),
  ...HARDCODED_REGISTRY,
}

// ---- Category Groups (8 categories for palette) ----

export const actionCategories: ActionCategoryGroup[] = [
  {
    label: 'Data Fetch',
    icon: 'Database',
    color: 'text-primary',
    actions: ['restful_data_action', 'graphql_action', 'supabase_select_action', 'gemini_action'],
  },
  {
    label: 'Data Mutate',
    icon: 'Pencil',
    color: 'text-primary',
    actions: ['upsertWhere', 'compute', 'updateWhere', 'removeWhere', 'supabase_insert_action', 'supabase_update_action', 'supabase_delete_action', 'set_state_action', 'expression_action'],
  },
  {
    label: 'Navigation',
    icon: 'Compass',
    color: 'text-blue-500',
    actions: ['navigation_action', 'go_back_action'],
  },
  {
    label: 'External Link',
    icon: 'ExternalLink',
    color: 'text-blue-500',
    actions: ['open_link_action'],
  },
  {
    label: 'Event',
    icon: 'Zap',
    color: 'text-indigo-400',
    actions: ['emitEvent'],
  },
  {
    label: 'UI Motion',
    icon: 'RefreshCw',
    color: 'text-indigo-400',
    actions: ['runMotion'],
  },
  {
    label: 'UI Feedback',
    icon: 'Bell',
    color: 'text-indigo-400',
    actions: ['show_toast_action', 'show_popup_action', 'close_popup_action', 'open_drawer_action', 'close_drawer_action', 'set_property_action', 'refresh_action'],
  },
  {
    label: 'Form',
    icon: 'CheckSquare',
    color: 'text-violet-500',
    actions: ['form_validate_action', 'form_reset_action', 'form_submit_action'],
  },
  {
    label: 'Flow Control',
    icon: 'GitBranch',
    color: 'text-amber-500',
    actions: ['conditional_action', 'loop_action', 'switch_action', 'run_action_flow_action', 'delay_action', 'break_action'],
  },
  {
    label: 'Auth',
    icon: 'Shield',
    color: 'text-rose-500',
    actions: ['login_action', 'logout_action', 'register_action'],
  },
  {
    label: 'Utility',
    icon: 'Wrench',
    color: 'text-gray-400',
    actions: ['console_log_action', 'storage_get_action', 'storage_set_action', 'storage_remove_action', 'hash_action', 'encrypt_action', 'decrypt_action'],
  },
]

/** Get config for an action type, with fallback. */
export function getActionConfig(actionType: string): ActionTypeConfig {
  return actionRegistry[actionType] ?? {
    label: actionType,
    icon: 'Play',
    category: 'utility' as VisualCategory,
    badgeColor: 'bg-gray-500',
    formKey: 'generic',
    description: actionType,
  }
}

/** Count how many action types were added from actions.json (not in hardcoded). */
export function getDynamicActionCount(): number {
  const dynamicKeys = Object.keys(buildDynamicRegistry())
  const hardcodedKeys = new Set(Object.keys(HARDCODED_REGISTRY))
  return dynamicKeys.filter((k) => !hardcodedKeys.has(k)).length
}

// ---- 3-Tier System ----

export type ActionTier = 'data' | 'interaction' | 'control'

/**
 * Storage actions live in 'utility' VisualCategory but belong to the 'data' tier.
 * We split them out explicitly so the palette can show them under Data.
 */
const STORAGE_ACTIONS = new Set([
  'storage_get_action',
  'storage_set_action',
  'storage_remove_action',
])

export const TIER_CONFIG: Record<ActionTier, {
  label: string
  icon: string
  categories: VisualCategory[]
  description: string
}> = {
  data: {
    label: 'Data',
    icon: 'Database',
    categories: ['dataFetch', 'dataMutate'],
    description: 'Fetch, mutate & store data',
  },
  interaction: {
    label: 'Interaction',
    icon: 'MousePointerClick',
    categories: ['navigation', 'uiFeedback', 'form'],
    description: 'Navigation, UI & forms',
  },
  control: {
    label: 'Control',
    icon: 'GitBranch',
    categories: ['flowControl', 'auth', 'utility'],
    description: 'Logic, auth & utilities',
  },
}

/** Map a VisualCategory → ActionTier. Storage actions override utility→data. */
export function getTierForCategory(category: VisualCategory): ActionTier {
  for (const [tier, config] of Object.entries(TIER_CONFIG)) {
    if (config.categories.includes(category)) return tier as ActionTier
  }
  return 'control' // fallback
}

/**
 * Storage sub-group extracted from Utility for the Data tier.
 */
const STORAGE_GROUP: ActionCategoryGroup = {
  label: 'Storage',
  icon: 'HardDrive',
  color: 'text-primary',
  actions: [...STORAGE_ACTIONS],
}

/**
 * Get ActionCategoryGroups for a given tier.
 *
 * Storage actions (storage_get/set/remove) are moved from Utility to Data tier
 * as a dedicated "Storage" sub-group.
 */
export function getActionsByTier(tier: ActionTier): ActionCategoryGroup[] {
  const config = TIER_CONFIG[tier]
  const groups = actionCategories
    .filter((cat) => {
      // Find the VisualCategory for this group by checking first action
      const firstAction = cat.actions[0]
      if (!firstAction) return false
      const actionConfig = actionRegistry[firstAction]
      if (!actionConfig) return false
      return config.categories.includes(actionConfig.category)
    })
    .map((cat) => {
      // For 'control' tier, strip storage actions from Utility group
      if (tier === 'control' && cat.label === 'Utility') {
        return {
          ...cat,
          actions: cat.actions.filter((a) => !STORAGE_ACTIONS.has(a)),
        }
      }
      return cat
    })
    .filter((cat) => cat.actions.length > 0)

  // Append Storage sub-group to data tier
  if (tier === 'data') {
    groups.push(STORAGE_GROUP)
  }

  return groups
}

/** Tier color tokens for use in other components (e.g. flow-group-list). */
export const TIER_COLORS: Record<ActionTier, string> = {
  data: 'sky',
  interaction: 'blue',
  control: 'amber',
}
